import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";

const emailSchema = z.string().trim().email({ message: "Please enter a valid email address" });

const SignupSection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      toast({
        title: "Invalid email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Check if already on waitlist
      const checkResponse = await fetch(`/api/waitlist/${encodeURIComponent(validation.data)}`);
      const checkData = await checkResponse.json();

      if (checkData.isOnWaitlist) {
        toast({
          title: "You're already on the list!",
          description: "We'll be in touch soon.",
        });
        setIsSuccess(true);
        return;
      }

      // Add to waitlist via D1 API
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: validation.data }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        toast({
          title: "You're on the list!",
          description: "We'll be in touch soon.",
        });

        // Send welcome email via Cloudflare Pages Function (fire and forget)
        console.log('Attempting to send welcome email to:', validation.data);
        fetch("/send-welcome-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: validation.data }),
        }).catch((err) => console.error("Failed to send welcome email:", err));
      } else {
        throw new Error(data.error || "Failed to add to waitlist");
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="signup" className="section-padding bg-gradient-hero">
        <div className="section-container text-center">
          <div className="max-w-md mx-auto p-8 rounded-2xl bg-card shadow-lg border border-border/50">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
              <svg className="w-8 h-8 text-secondary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">You're on the list!</h2>
            <p className="text-muted-foreground">We'll be in touch soon.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="signup" className="section-padding bg-gradient-hero">
      <div className="section-container text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
          Join the Waitlist
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          Be first to try it. Help shape what gets built.
        </p>

        <form onSubmit={handleSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 h-12 px-4 text-base bg-card border-border/50 focus:border-primary"
            disabled={isLoading}
          />
          <Button
            type="submit"
            variant="cta"
            size="lg"
            disabled={isLoading}
            className="sm:w-auto w-full"
          >
            {isLoading ? "Joining..." : "Get Early Access"}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default SignupSection;